import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { productPanelId } from './product-switcher-ids';
import { useProductSwitcher } from './product-switcher-context';
import { getShortcutKeys, getShortcutLabel, readUserAgent } from './shortcut-label';

/**
 * The searching half of the context bar. It searches one list at a time and says which one;
 * at rest it is searching nothing, so it claims nothing and shows the organization you are in.
 */
export function ContextSearch() {
  const switcher = useProductSwitcher();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isOpen = switcher !== null && switcher.isPanelOpen;
  const userAgent = readUserAgent();

  // Opening the menu puts the cursor where the typing goes, however it was opened.
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  if (switcher === null) {
    return null;
  }

  let label = 'Search';
  if (isOpen && switcher.mode === 'organizations') {
    label = 'Search organizations';
  } else if (isOpen) {
    label = 'Search products';
  }

  // Resting, the bar names where you are — and says nothing at all until it knows. The
  // organization arrives a fetch after the first paint, so any stand-in here is a word the
  // member watches get replaced. The control still has its own name for a screen reader.
  let placeholder = '';
  if (switcher.selectedOrganization && switcher.selectedOrganization.name.length > 0) {
    placeholder = switcher.selectedOrganization.name;
  }
  if (isOpen) {
    placeholder = label;
  }

  return (
    <>
      <IconButton
        onClick={() => switcher.openPanel('organizations')}
        aria-label={label}
        sx={{
          borderRadius: 0,
          px: 1.25,
          color: 'text.secondary',
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
        }}
      >
        <SearchRoundedIcon sx={{ fontSize: 20 }} aria-hidden="true" />
      </IconButton>
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, pr: 1 }}>
        <InputBase
          inputRef={inputRef}
          value={switcher.query}
          onChange={(event) => switcher.setQuery(event.target.value)}
          onFocus={() => {
            // Focusing an already-open menu must not change what it is searching.
            if (!isOpen) {
              switcher.openPanel('organizations');
            }
          }}
          placeholder={placeholder}
          type="search"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: '15px',
            '& input::-webkit-search-cancel-button': { display: 'none' },
          }}
          // A search field that filters the region below it — not a combobox: there is no
          // listbox, no arrow-key walk and no active option, so claiming one promises a way
          // through the results that does not exist. The product half carries the disclosure.
          inputProps={{
            'aria-label': label,
            'aria-controls': productPanelId,
            'aria-keyshortcuts': getShortcutKeys(userAgent),
          }}
        />
        {/* Hidden from the reader: aria-keyshortcuts on the input above already says this, and
            as a name or description it would be read out on every visit to the field. */}
        <Box
          component="kbd"
          aria-hidden="true"
          sx={{
            flexShrink: 0,
            ml: 1,
            display: { xs: 'none', sm: 'block' },
            color: 'text.secondary',
            fontFamily: 'inherit',
            fontSize: '12px',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {getShortcutLabel(userAgent)}
        </Box>
      </Box>
    </>
  );
}
